import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api/api";
import {
  Paper, Typography, Grid, TextField, MenuItem,
  Button, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, IconButton, Chip, Box, TablePagination, Divider,
  Checkbox, Tooltip, CircularProgress, Alert, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArticleIcon from "@mui/icons-material/Article";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ConfirmDialog from "../components/ConfirmDialog";

const baseURL = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === "production" ? "" : "http://127.0.0.1:8000");

const ChipResultado = ({ valor }) =>
  valor === "aprobado"
    ? <Chip label="Passed" color="success" size="small" />
    : <Chip label="Not passed" color="error" size="small" />;

const ESTADO_CFG = {
  pendiente:      { label: "Pending",   color: "#E65100" },
  vencido:        { label: "Expired",   color: "#C62828" },
  desactualizado: { label: "Outdated",  color: "#757575" },
};

const ChipEstado = ({ estado }) => {
  const cfg = ESTADO_CFG[estado] || { label: estado, color: "#555" };
  return (
    <Chip label={cfg.label} size="small"
      sx={{ bgcolor: cfg.color, color: "white", fontWeight: 600, fontSize: "11px" }} />
  );
};

export default function Capacitaciones() {

  // ─── Catalogs ─────────────────────────────────────────────
  const [cursos, setCursos] = useState([]);          // only those with pending items (record selector)
  const [cursosHistorial, setCursosHistorial] = useState([]); // all (history filter)
  const [empleadosSelect, setEmpleadosSelect] = useState([]);

  // ─── Record form ────────────────────────────────────────
  const [idCurso, setIdCurso] = useState("");
  const [versionInfo, setVersionInfo] = useState(null);
  const [fecha, setFecha] = useState("");
  const [horas, setHoras] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [fileKey, setFileKey] = useState(Date.now());
  const [empRows, setEmpRows] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [busquedaEmp, setBusquedaEmp] = useState("");
  const [registrando, setRegistrando] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── History ──────────────────────────────────────────────
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [filtroEmp, setFiltroEmp] = useState(null);
  const [filtroCursoHist, setFiltroCursoHist] = useState("");
  const [filtroResultado, setFiltroResultado] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editHoras, setEditHoras] = useState("");
  const [editFecha, setEditFecha] = useState("");
  const [editResultado, setEditResultado] = useState("aprobado");
  const [fichasModal, setFichasModal] = useState(null); // { version, fichas[] }

  // ─── Initial load ─────────────────────────────────────────
  const cargarCursosRegistro = useCallback(() => {
    api.get("/cursos", { params: { con_pendientes: true } })
      .then(r => setCursos(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargarCursosRegistro();
    api.get("/cursos").then(r => setCursosHistorial(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/empleados/select").then(r => setEmpleadosSelect(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [cargarCursosRegistro]);

  // ─── On course change ───────────────────────────────────
  useEffect(() => {
    if (!idCurso) { setVersionInfo(null); setEmpRows([]); return; }

    api.get("/versiones", { params: { id_curso: idCurso } }).then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      const vigente = lista.find(v => v.vigente) ?? lista[0] ?? null;
      setVersionInfo(vigente ? { id_version: vigente.id_version, numero_version: vigente.numero_version, duracion_horas: vigente.duracion_horas } : null);
      setHoras(vigente?.duracion_horas != null ? String(vigente.duracion_horas) : "");
    }).catch(() => setVersionInfo(null));

    setLoadingEmps(true);
    setBusquedaEmp("");
    api.get(`/cursos/${idCurso}/empleados`)
      .then(r => setEmpRows(
        (Array.isArray(r.data) ? r.data : []).map(e => ({ ...e, selected: true, resultado: "aprobado" }))
      ))
      .catch(() => setEmpRows([]))
      .finally(() => setLoadingEmps(false));
  }, [idCurso]);

  // ─── History ──────────────────────────────────────────────
  const cargarHistorial = useCallback(async () => {
    try {
      const params = {};
      if (filtroEmp)       params.id_empleado = filtroEmp.id_empleado;
      if (filtroCursoHist) params.id_curso    = filtroCursoHist;
      if (filtroResultado) params.resultado   = filtroResultado;
      const res = await api.get("/capacitaciones/con_detalle", { params });
      setCapacitaciones(Array.isArray(res.data) ? res.data : []);
      setPage(0);
    } catch { setCapacitaciones([]); }
  }, [filtroEmp, filtroCursoHist, filtroResultado]);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  // ─── Employee table helpers ────────────────────────────────
  const empsFiltrados = useMemo(
    () => empRows.filter(e => e.nombre_completo.toLowerCase().includes(busquedaEmp.toLowerCase())),
    [empRows, busquedaEmp]
  );

  const seleccionados   = empRows.filter(e => e.selected);
  const todosSelected   = empRows.length > 0 && empRows.every(e => e.selected);
  const algunoSelected  = empRows.some(e => e.selected);

  const toggleAll      = () => setEmpRows(rows => rows.map(r => ({ ...r, selected: !todosSelected })));
  const toggleEmp      = id => setEmpRows(rows => rows.map(r => r.id_empleado === id ? { ...r, selected: !r.selected } : r));
  const toggleResultado = id => setEmpRows(rows => rows.map(r =>
    r.id_empleado === id ? { ...r, resultado: r.resultado === "aprobado" ? "no_aprobado" : "aprobado" } : r
  ));

  // ─── Register ─────────────────────────────────────────────
  const registrar = async () => {
    if (!idCurso || !versionInfo || !fecha) { alert("Select the course and the date."); return; }
    if (seleccionados.length === 0) { alert("Select at least one employee."); return; }

    setRegistrando(true);
    try {
      const form = new FormData();
      form.append("id_curso", idCurso);
      form.append("id_version", versionInfo.id_version);
      form.append("fecha_capacitacion", fecha);
      if (horas !== "") form.append("horas", horas);
      form.append("registros", JSON.stringify(
        seleccionados.map(e => ({ id_empleado: e.id_empleado, resultado: e.resultado }))
      ));
      if (archivo) form.append("file", archivo);

      await api.post("/capacitaciones_upload/bulk", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setSuccessMsg(`${seleccionados.length} record(s) saved successfully.`);
      setTimeout(() => setSuccessMsg(""), 5000);
      setEmpRows(rows => rows.map(r => ({ ...r, selected: false, resultado: "aprobado" })));
      setArchivo(null);
      setFileKey(Date.now());
      setFecha("");
      cargarHistorial();
      cargarCursosRegistro();
    } catch (err) {
      alert(err.response?.data?.detail ?? "Error registering. Check that there are no duplicates.");
    } finally {
      setRegistrando(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────
  const eliminar = async () => {
    try {
      await api.delete(`/capacitaciones/${deleteId}`);
      setConfirmOpen(false);
      cargarHistorial();
    } catch { alert("Error deleting record."); }
  };

  const abrirEdicion = (c) => {
    setEditRow(c);
    setEditFecha(c.fecha_capacitacion ?? "");
    setEditHoras(c.horas != null ? String(c.horas) : "");
    setEditResultado(c.resultado ?? "aprobado");
  };

  const guardarEdicion = async () => {
    try {
      const payload = {
        fecha_capacitacion: editFecha || null,
        resultado: editResultado,
        horas: editHoras !== "" ? parseFloat(editHoras) : null,
      };
      await api.patch(`/capacitaciones/${editRow.id_curso_empleado}`, payload);
      setEditRow(null);
      cargarHistorial();
    } catch (e) {
      alert(e.response?.data?.detail ?? "Error saving changes.");
    }
  };

  const capPaginadas = capacitaciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ─── Render ───────────────────────────────────────────────
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Training Record</Typography>

      {/* ══ FORM ═══════════════════════════════════════════ */}
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={4}>
          <TextField
            select fullWidth label="Course"
            value={idCurso} onChange={e => setIdCurso(e.target.value)}
          >
            <MenuItem value="">— Select a course —</MenuItem>
            {cursos.map(c => <MenuItem key={c.id_curso} value={c.id_curso}>{c.nombre_curso}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
          {versionInfo
            ? <Chip label={`v${versionInfo.numero_version} (current)`} color="success" variant="outlined" />
            : idCurso ? <Chip label="No current version" color="warning" variant="outlined" /> : null}
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            type="date" fullWidth label="Training date"
            value={fecha} InputLabelProps={{ shrink: true }}
            onChange={e => setFecha(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            type="number" fullWidth label="Hours"
            value={horas}
            onChange={e => setHoras(e.target.value)}
            inputProps={{ min: 0.5, max: 999, step: 0.5 }}
            InputLabelProps={{ shrink: true }}
            helperText={versionInfo?.duracion_horas ? `Standard: ${versionInfo.duracion_horas}h` : ""}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <Button
            variant="outlined" component="label"
            startIcon={<AttachFileIcon />} fullWidth
            color={archivo ? "success" : "inherit"}
          >
            {archivo ? archivo.name : "Attach PDF (optional)"}
            <input
              key={fileKey} type="file" accept=".pdf" hidden
              onChange={e => setArchivo(e.target.files[0] || null)}
            />
          </Button>
        </Grid>
      </Grid>

      {/* ── Employee table ── */}
      {idCurso && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Employees requiring training
              {empRows.length > 0 && (
                <Chip
                  label={`${seleccionados.length} / ${empRows.length} selected`}
                  size="small" sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <TextField
              size="small" placeholder="Search employee..."
              value={busquedaEmp} onChange={e => setBusquedaEmp(e.target.value)}
              sx={{ width: 220 }}
            />
          </Box>

          {loadingEmps ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#000" }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={todosSelected}
                      indeterminate={algunoSelected && !todosSelected}
                      onChange={toggleAll}
                      sx={{ color: "white", "&.Mui-checked,&.MuiCheckbox-indeterminate": { color: "white" } }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>Name</TableCell>
                  <TableCell sx={{ color: "white" }}>Position</TableCell>
                  <TableCell sx={{ color: "white" }}>Area</TableCell>
                  <TableCell sx={{ color: "white" }} align="center">Reason</TableCell>
                  <TableCell sx={{ color: "white" }} align="center">Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {empsFiltrados.map(row => (
                  <TableRow
                    key={row.id_empleado} hover
                    sx={{ opacity: row.selected ? 1 : 0.4, transition: "opacity .15s" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={row.selected} onChange={() => toggleEmp(row.id_empleado)} />
                    </TableCell>
                    <TableCell>{row.nombre_completo}</TableCell>
                    <TableCell>{row.puesto_texto}</TableCell>
                    <TableCell>{row.area_texto}</TableCell>
                    <TableCell align="center">
                      <ChipEstado estado={row.estado} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <Switch
                          size="small" color="success"
                          checked={row.resultado === "aprobado"}
                          onChange={() => toggleResultado(row.id_empleado)}
                          disabled={!row.selected}
                        />
                        <ChipResultado valor={row.resultado} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {empsFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      {empRows.length === 0
                        ? "All employees are up to date on this course ✓"
                        : "No matches for the search"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {successMsg && <Alert severity="success" sx={{ mt: 2 }}>{successMsg}</Alert>}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained" size="large" onClick={registrar}
              disabled={registrando || seleccionados.length === 0 || !fecha || !versionInfo}
            >
              {registrando ? "Registering…" : `Register ${seleccionados.length} employee(s)`}
            </Button>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* ══ HISTORY ═════════════════════════════════════════ */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5">Training history</Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Autocomplete
            options={empleadosSelect}
            getOptionLabel={e => e.nombre_completo ?? ""}
            value={filtroEmp}
            onChange={(_, v) => setFiltroEmp(v)}
            isOptionEqualToValue={(o, v) => o.id_empleado === v.id_empleado}
            renderInput={params => <TextField {...params} label="Employee" size="small" />}
            sx={{ width: 220 }}
          />
          <TextField
            select size="small" label="Course" value={filtroCursoHist}
            onChange={e => setFiltroCursoHist(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {cursosHistorial.map(c => <MenuItem key={c.id_curso} value={c.id_curso}>{c.nombre_curso}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Result" value={filtroResultado}
            onChange={e => setFiltroResultado(e.target.value)} sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="aprobado">Passed</MenuItem>
            <MenuItem value="no_aprobado">Not passed</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Employee</TableCell>
            <TableCell sx={{ color: "white" }}>Course</TableCell>
            <TableCell sx={{ color: "white" }}>Version</TableCell>
            <TableCell sx={{ color: "white" }}>Date</TableCell>
            <TableCell sx={{ color: "white" }}>Hours</TableCell>
            <TableCell sx={{ color: "white" }}>Expiration</TableCell>
            <TableCell sx={{ color: "white" }}>Result</TableCell>
            <TableCell sx={{ color: "white" }}>Evidence</TableCell>
            <TableCell sx={{ color: "white" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {capPaginadas.map(c => (
            <TableRow key={c.id_curso_empleado} hover>
              <TableCell>{c.nombre_empleado}</TableCell>
              <TableCell>{c.nombre_curso}</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{c.numero_version}</span>
                  {c.fichas?.length > 0 && (
                    <Tooltip title={`${c.fichas.length} working sheet(s) changed`}>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => setFichasModal({ version: c.numero_version, fichas: c.fichas })}
                      >
                        <ArticleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>{c.fecha_capacitacion}</TableCell>
              <TableCell>{c.horas != null ? `${c.horas}h` : "—"}</TableCell>
              <TableCell>
                {c.fecha_vencimiento
                  ? c.fecha_vencimiento
                  : c.es_desactualizado
                    ? <Chip label="Outdated" size="small"
                        sx={{ bgcolor: ESTADO_CFG.desactualizado.color, color: "white", fontSize: "11px" }} />
                    : <span style={{ color: "#888", fontStyle: "italic" }}>Until new version</span>}
              </TableCell>
              <TableCell><ChipResultado valor={c.resultado} /></TableCell>
              <TableCell>
                {c.evidencia_url
                  ? <Tooltip title="Download evidence">
                      <a href={`${baseURL}/${c.evidencia_url.replace(/\\/g, "/")}`} target="_blank" rel="noreferrer">
                        <IconButton size="small"><DownloadIcon /></IconButton>
                      </a>
                    </Tooltip>
                  : "—"}
              </TableCell>
              <TableCell>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => abrirEdicion(c)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small"
                    onClick={() => { setDeleteId(c.id_curso_empleado); setConfirmOpen(true); }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {capPaginadas.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                No records
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div" count={capacitaciones.length} page={page}
        onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Rows per page"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
      />

      {/* Edit dialog */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit record</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {editRow && (
            <Typography variant="body2" color="text.secondary">
              {editRow.nombre_empleado} — {editRow.nombre_curso}
            </Typography>
          )}
          <TextField
            type="date" label="Training date" size="small"
            value={editFecha} onChange={e => setEditFecha(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
          />
          <TextField
            type="number" label="Hours" size="small"
            value={editHoras} onChange={e => setEditHoras(e.target.value)}
            inputProps={{ min: 0.5, max: 999, step: 0.5 }}
            helperText="Leave empty to use the version's standard hours"
            fullWidth
          />
          <TextField
            select label="Result" size="small"
            value={editResultado} onChange={e => setEditResultado(e.target.value)}
            fullWidth
          >
            <MenuItem value="aprobado">Passed</MenuItem>
            <MenuItem value="no_aprobado">Not passed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={guardarEdicion}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Working sheet history modal */}
      <Dialog open={!!fichasModal} onClose={() => setFichasModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Working sheets changed — version {fichasModal?.version}
        </DialogTitle>
        <DialogContent dividers>
          {fichasModal?.fichas.map(f => (
            <Box
              key={f.codigo}
              sx={{
                display: "flex", flexDirection: "column",
                borderBottom: "1px solid", borderColor: "divider",
                py: 1.5, "&:last-child": { borderBottom: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="body2" fontWeight={700}>{f.codigo}</Typography>
                {f.version && (
                  <Chip label={`Rev. ${f.version}`} size="small" color="primary" variant="outlined"
                    sx={{ fontSize: "10px", height: 18, "& .MuiChip-label": { px: 0.8 } }} />
                )}
                {f.archivo_url && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label="View PDF"
                    size="small"
                    color="error"
                    variant="outlined"
                    clickable
                    component="a"
                    href={`${baseURL}/${f.archivo_url.replace(/^fichas\//, "fichas_archivos/")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ ml: "auto" }}
                  />
                )}
              </Box>
              {f.nombre && f.nombre !== f.codigo && (
                <Typography variant="caption" color="text.secondary">{f.nombre}</Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFichasModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete this training record?"
        onConfirm={eliminar}
      />
    </Paper>
  );
}
